import type { Plan } from '@/config/types/Plan'
import type { Retailer } from '@/config/types/Retailer'

export interface RetailerOption {
  id: string | number
  label: string
  bank_code?: string
}

export interface RetailerSelectOption {
  label: string
  value: string | number
}

export function extractUniqueRetailers(plans: Plan[], activeOnly = false): Retailer[] {
  const retailerMap = new Map<string | number, Retailer>()

  plans.forEach((plan) => {
    if (activeOnly && !plan.active) return

    if (plan.retailers && plan.retailers.length > 0) {
      plan.retailers.forEach((retailer) => {
        if (!retailerMap.has(retailer.id)) {
          retailerMap.set(retailer.id, retailer)
        }
      })
    }
  })

  return Array.from(retailerMap.values())
}

export function extractRetailerOptions(plans: Plan[], activeOnly = false): RetailerOption[] {
  const retailerMap = new Map<string | number, RetailerOption>()

  plans.forEach((plan) => {
    // if (activeOnly && !plan.active) return

    if (plan.retailers && plan.retailers.length > 0) {
      plan.retailers.forEach((retailer) => {
        if (!retailerMap.has(retailer.id)) {
          retailerMap.set(retailer.id, {
            id: retailer.id,
            label: retailer.label,
            bank_code: retailer.bank_code,
          })
        }
      })
    }
  })

  return Array.from(retailerMap.values())
}

export function extractRetailerSelectOptions(
  plans: Plan[],
  activeOnly = false,
): RetailerSelectOption[] {
  const retailerMap = new Map<string | number, RetailerSelectOption>()

  plans.forEach((plan) => {
    if (activeOnly && !plan.active) return

    const retailer = plan.retailers?.[0]
    if (retailer && !retailerMap.has(retailer.id)) {
      retailerMap.set(retailer.id, {
        label: retailer.label,
        value: retailer.id,
      })
    }
  })

  return Array.from(retailerMap.values())
}

export function extractUniqueKpiAreas(plans: Plan[]): string[] {
  const kpiSet = new Set<string>()

  plans.forEach((plan) => {
    plan.actions?.forEach((action) => {
      if (action.kpiArea) {
        kpiSet.add(action.kpiArea)
      }
    })
  })

  return Array.from(kpiSet).sort()
}

export function countTotalActions(plans: Plan[], activeOnly = false): number {
  return plans.reduce((total, plan) => {
    if (activeOnly && !plan.active) return total
    return total + (plan.actions?.length || 0)
  }, 0)
}

export function countActionsByState(plans: Plan[]): {
  pending: number
  active: number
  complete: number
} {
  const counts = { pending: 0, active: 0, complete: 0 }

  plans.forEach((plan) => {
    plan.actions?.forEach((action) => {
      counts[action.state]++
    })
  })

  return counts
}
